import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucidePlug, lucidePrinter, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmDialog, HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmInput } from '@spartan-ng/helm/input';
import { HlmSheet, HlmSheetImports } from '@spartan-ng/helm/sheet';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';

import { closeLoader, dispararLoader } from 'src/app/helpers/swall.helpers';
import { LabelEditorComponent } from 'src/app/shared/custom-components/label-editor/label-editor.component';
import type { LabelLayoutConfig } from 'src/app/services/niimbot-printer/niimbot-printer.models';
import {
  NiimbotPrinterService,
  type NiimbotConnectionStatus,
} from 'src/app/services/niimbot-printer/niimbot-printer.service';

import {
  avisoResultadoLote,
  camposExtras,
  dadosImpressao,
  imprimirEmLote,
  impedimento,
  layoutSalvo,
  montarLayout,
  percentualLote,
  resumoSelecao,
  valoresIniciaisExtras,
  type IngressoEtiqueta,
} from './checkin-etiqueta';

/**
 * Impressão de etiqueta térmica de um ou de VÁRIOS ingressos.
 *
 * Um só e um lote são o mesmo modal porque são a mesma decisão: qual layout,
 * qual impressora, quais campos. O que muda é a quantidade — por isso o estado
 * é sempre uma LISTA (`ingressos`), e `abrir()` é `abrirLote()` com um item.
 *
 * Só a casca visual: diálogo no desktop, bottom sheet no mobile, com o MESMO
 * corpo (`#corpo`) e as MESMAS ações (`#acoes`) nos dois — o modal anterior
 * existia numa forma só e ficava espremido no celular, que é justamente onde a
 * impressora térmica é usada. Toda a lógica (o que impede imprimir, como o
 * layout é montado) mora em `checkin-etiqueta.ts`, sem Angular e com teste.
 *
 * Medidas do padrão atual de modal (o mesmo do QR do lote): cabeçalho e rodapé
 * com `px-6 py-5` e borda, título `font-display`, `rounded-card`,
 * `shadow-float`.
 *
 * O componente NÃO possui a conexão da impressora — quem possui é o
 * `NiimbotPrinterService` (`providedIn: 'root'`), o mesmo que o
 * `app-checkin-impressora` usa. Aqui só se LÊ o status; conectar é com o
 * gerenciador, e é para lá que a saída `conectar` manda o pai.
 *
 * ```html
 * <app-checkin-etiqueta #etiqueta (conectar)="impressora.abrir()" />
 * <button (click)="etiqueta.abrir(ingresso)">Imprimir etiqueta</button>
 * <button (click)="etiqueta.abrirLote(selecionados)">Imprimir selecionadas</button>
 * ```
 */
@Component({
  selector: 'app-checkin-etiqueta',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    NgIcon,
    HlmButton,
    HlmInput,
    LabelEditorComponent,
    ...HlmDialogImports,
    ...HlmSheetImports,
  ],
  providers: [provideIcons({ lucidePrinter, lucidePlug, lucideTriangleAlert })],
  template: `
    <!--
      Os dois overlays existem sempre; abrir() escolhe qual abre (sem crase
      neste comentário: ele vive dentro de um template literal). Um @if em
      volta economizaria um host fechado e custaria o viewChild sumir na hora
      errada — e nenhum dos dois portaliza nada enquanto está fechado.

      admin-portal-scope repetido nos painéis: o CDK portaliza para fora do DOM
      do shell e o conteúdo perderia o escopo das utilities do anel.

      A largura é sm:max-w-[54rem], e não max-w-: a classe base do
      hlm-dialog-content já traz sm:max-w-md, que ganha de um max-w- sem
      variante e prendia o diálogo em 448px (medido).

      max-h-[90dvh] no content, e não só no pane: sem um teto AQUI a coluna
      flex não resolve, o corpo não rola e o rodapé sai da tela. Medido numa
      viewport de 680px, onde o botão Imprimir ficava inalcançável. Os 90 (era
      85) somam com o teto em dvh do canvas do editor para o conteúdo caber
      inteiro em tela baixa, em vez de rolar.
    -->
    <hlm-dialog #dialogo>
      <hlm-dialog-content
        *hlmDialogPortal
        [showCloseButton]="false"
        class="admin-portal-scope flex max-h-[90dvh] w-full flex-col gap-0 overflow-hidden rounded-card bg-card p-0 text-card-foreground shadow-float sm:max-w-[54rem]"
      >
        <!-- flex-row explícito: a diretiva hlmDialogHeader aplica flex-col e,
             sem isto, o botão de fechar cai embaixo do título em vez de ir
             para a direita (o modal de QR do lote tem o mesmo detalhe). -->
        <div
          hlmDialogHeader
          class="flex shrink-0 flex-row items-start justify-between gap-3 border-b border-border px-6 py-5"
        >
          <div class="min-w-0">
            <h3 hlmDialogTitle class="font-display text-base font-bold text-foreground">
              {{ titulo() }}
            </h3>
            <p class="mt-1 truncate text-xs text-muted-foreground">{{ resumo() }}</p>
          </div>
          <!-- showCloseButton=false acima: o content já desenha um X absoluto
               em end-4 top-4, e com o nosso do cabeçalho virava dois. -->
          <button type="button" hlmDialogClose hlmBtn variant="ghost" size="icon-sm" aria-label="Fechar">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
            </svg>
          </button>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto">
          <ng-template [ngTemplateOutlet]="corpo"></ng-template>
        </div>

        <div hlmDialogFooter class="shrink-0 border-t border-border px-6 py-5">
          <ng-template [ngTemplateOutlet]="acoes"></ng-template>
        </div>
      </hlm-dialog-content>
    </hlm-dialog>

    <hlm-sheet #folha side="bottom">
      <hlm-sheet-content
        *hlmSheetPortal
        class="admin-portal-scope flex max-h-[92dvh] flex-col gap-0 overflow-hidden p-0"
        [showCloseButton]="false"
      >
        <!-- gap-0 acima e padding curto aqui: a classe base do sheet traz gap-4
             e o header traz p-4, e os dois somados abriam um vão sob o
             subtítulo maior que a própria faixa de status. Em tela de celular
             essa altura é o que falta para os rótulos do editor aparecerem.
             92dvh (era 85) pelo mesmo motivo. -->
        <div class="mx-auto mt-2.5 h-[4px] w-[40px] shrink-0 rounded-full bg-border"></div>
        <hlm-sheet-header class="items-center gap-0.5 px-6 pt-3 pb-3 text-center">
          <h3 hlmSheetTitle class="font-display text-lg font-bold">{{ titulo() }}</h3>
          <p class="text-xs text-muted-foreground">{{ resumo() }}</p>
        </hlm-sheet-header>

        <div class="min-h-0 flex-1 overflow-y-auto">
          <ng-template [ngTemplateOutlet]="corpo"></ng-template>
        </div>

        <div class="flex shrink-0 flex-col-reverse gap-2 border-t border-border px-6 pt-3 pb-5">
          <ng-template [ngTemplateOutlet]="acoes"></ng-template>
        </div>
      </hlm-sheet-content>
    </hlm-sheet>

    <!-- Corpo compartilhado ------------------------------------------------ -->
    <ng-template #corpo>
      @if (impedido(); as imp) {
        <!-- Isto é o bug antigo virado tela: antes o clique sumia em silêncio.

             sticky porque o #corpo mora dentro do overflow-y-auto junto com o
             editor: rolando para ver a etiqueta, o aviso saía de vista e levava
             junto o botão Conectar — justamente a saída de um clique que ele
             existe para oferecer. bg-muted é opaco, o canvas não vaza por baixo. -->
        <div
          class="sticky top-0 z-10 flex flex-wrap items-start gap-3 border-b border-border bg-muted px-6 py-3 sm:items-center"
          role="status"
        >
          <!-- Ícone e texto na MESMA linha; só o botão desce no mobile. -->
          <div class="flex w-full min-w-0 items-start gap-3 sm:w-auto sm:flex-1">
            <ng-icon name="lucideTriangleAlert" class="mt-0.5 shrink-0 text-destructive" size="18px" />
            <div class="min-w-0">
              <p class="m-0 text-[13px] font-bold text-foreground">{{ imp.titulo }}</p>
              <p class="m-0 text-xs text-muted-foreground">{{ imp.detalhe }}</p>
            </div>
          </div>
          @if (imp.acao === 'conectar') {
            <button
              type="button"
              hlmBtn
              variant="outline"
              size="sm"
              class="h-[1.75rem] gap-1.5 px-2.5 text-[10pt] normal-case"
              (click)="conectar.emit()"
            >
              <ng-icon name="lucidePlug" size="14px" />
              Conectar
            </button>
          }
        </div>
      } @else {
        <div class="flex items-center gap-2 border-b border-border bg-muted px-6 py-3">
          <span class="size-2 shrink-0 rounded-full bg-[#00E35E]"></span>
          <span class="text-xs font-semibold text-[#0A7A35]">Impressora conectada</span>
          @if (detalheConexao(); as detalhe) {
            <span class="truncate text-xs text-muted-foreground">{{ detalhe }}</span>
          }
        </div>
      }

      <div class="flex flex-col gap-5 px-6 py-5">
        <app-label-editor
          [qrText]="primeiro()?.codigoIngresso"
          [participantName]="primeiro()?.nomeParticipante"
          [loteName]="primeiro()?.nomeLote"
          (layoutChange)="aoMudarLayout($event)"
          (participantNameChange)="nomeEditado.set($event)"
          (loteNameChange)="loteEditado.set($event)"
        />

        @if (emLote()) {
          <!-- O canvas mostra UM ingresso e aceita corrigir o nome com duplo
               clique. Num lote esse nome é de um só, então a correção fica na
               prévia e não vai para o papel (ver imprimir()). Dizer isso aqui é
               mais barato que descobrir depois de 40 etiquetas erradas. -->
          <p class="m-0 rounded-card bg-muted px-4 py-3 text-xs text-muted-foreground">
            A prévia mostra o primeiro ingresso. Cada etiqueta sai com o nome, o
            código e o lote do seu próprio ingresso — o nome corrigido no canvas
            vale só para a prévia.
          </p>
        }

        @if (extras().length > 0) {
          <div class="flex flex-col gap-3">
            <h4 class="m-0 text-[13px] font-bold text-muted-foreground">Campos da etiqueta</h4>
            @if (emLote()) {
              <!-- Estes campos são digitados pelo operador, não vêm do
                   ingresso: pedir um valor por etiqueta não escala num lote,
                   então o valor digitado aqui se repete em todas. -->
              <p class="m-0 text-xs text-muted-foreground">
                O mesmo valor vai nas {{ ingressos().length }} etiquetas.
              </p>
            }
            @for (campo of extras(); track campo.id) {
              <label class="flex flex-col gap-1.5">
                <span class="text-xs font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                  {{ campo.value || campo.id }}
                </span>
                <input
                  hlmInput
                  type="text"
                  class="w-full"
                  [placeholder]="campo.value || 'Preencha…'"
                  [value]="valoresExtras()[campo.id] ?? ''"
                  (input)="definirExtra(campo.id, $any($event.target).value)"
                />
              </label>
            }
          </div>
        }
      </div>
    </ng-template>

    <!-- Ações compartilhadas ----------------------------------------------- -->
    <!-- Mesma medida do botão de ação da linha da tabela (data-table:
         h-[1.75rem] px-2.5 text-[10pt] normal-case). O rodapé de modal do DS
         usa o botão padrão de 52px, que nesta tela ficava desproporcional.
         w-full só no mobile: o alvo cresce na largura, não na altura. -->
    <ng-template #acoes>
      @if (imprimindoLote()) {
        <!-- Durante o lote o rodapé vira o progresso: o loader de tela cheia
             que a impressão individual usa esconderia justamente o número que
             o operador precisa ver, e a única ação que resta é parar.

             A barra é uma div com width em %, não um <progress>: o elemento
             nativo traz aparência própria por navegador e não segue os tokens
             do anel. role/aria-valuenow abaixo cobrem o leitor de tela. -->
        <div class="flex w-full flex-col gap-2">
          <div class="flex items-baseline justify-between gap-3">
            <span class="text-[13px] font-semibold text-foreground">
              Etiqueta {{ loteConcluidas() + 1 }} de {{ ingressos().length }}
            </span>
            <span class="text-xs text-muted-foreground">{{ progressoLote() }}%</span>
          </div>
          <div
            class="h-1.5 w-full overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-label="Progresso da impressão em lote"
            [attr.aria-valuenow]="progressoLote()"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="h-full rounded-full bg-primary transition-[width] duration-200"
              [style.width.%]="progressoLote()"
            ></div>
          </div>
          @if (nomeEmImpressao(); as nome) {
            <span class="truncate text-xs text-muted-foreground">{{ nome }}</span>
          }
          <!-- Parar não corta a etiqueta em curso: ela termina e o lote para na
               próxima (ver imprimirEmLote). Daí "Parando…" existir. -->
          <button
            type="button"
            hlmBtn
            variant="outline"
            size="sm"
            class="h-[1.75rem] w-full px-2.5 text-[10pt] normal-case"
            [disabled]="parando()"
            (click)="parando.set(true)"
          >
            {{ parando() ? 'Parando…' : 'Parar' }}
          </button>
        </div>
      } @else {
        <button
          type="button"
          hlmBtn
          variant="outline"
          size="sm"
          [class]="
            mobile()
              ? 'h-[1.75rem] w-full px-2.5 text-[10pt] normal-case'
              : 'h-[1.75rem] px-2.5 text-[10pt] normal-case'
          "
          (click)="fechar()"
        >
          Cancelar
        </button>
        <button
          type="button"
          hlmBtn
          variant="solid"
          size="sm"
          [class]="
            mobile()
              ? 'h-[1.75rem] w-full gap-1.5 px-2.5 text-[10pt] normal-case'
              : 'h-[1.75rem] gap-1.5 px-2.5 text-[10pt] normal-case'
          "
          [disabled]="!!impedido() || imprimindo()"
          (click)="imprimir()"
        >
          <ng-icon name="lucidePrinter" size="14px" />
          {{ rotuloImprimir() }}
        </button>
      }
    </ng-template>
  `,
})
export class CheckinEtiquetaComponent {
  private readonly niimbot = inject(NiimbotPrinterService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly dialogo = viewChild.required<HlmDialog>('dialogo');
  private readonly folha = viewChild.required<HlmSheet>('folha');

  /** O pai abre o gerenciador de impressora; conectar não é assunto daqui. */
  readonly conectar = output<void>();
  readonly impresso = output<void>();

  /**
   * A seleção. Sempre lista, mesmo com um item — foi o que evitou duplicar
   * impedimento, resumo e impressão em "modo lote" e "modo individual".
   */
  protected readonly ingressos = signal<readonly IngressoEtiqueta[]>([]);
  /** Quem o editor desenha na prévia. Num lote, o primeiro da seleção. */
  protected readonly primeiro = computed(() => this.ingressos()[0] ?? null);
  protected readonly emLote = computed(() => this.ingressos().length > 1);

  protected readonly layout = signal<LabelLayoutConfig | null>(null);
  protected readonly valoresExtras = signal<Record<string, string>>({});

  /**
   * Edição feita com duplo clique no canvas. O editor não persiste isso no
   * layout (é do ingresso, não do desenho), então quem segura é o pai — e é
   * daqui que sai o valor impresso.
   */
  protected readonly nomeEditado = signal<string | null>(null);
  protected readonly loteEditado = signal<string | null>(null);
  protected readonly imprimindo = signal(false);

  /** Etiquetas já concluídas do lote em curso. */
  protected readonly loteConcluidas = signal(0);
  /** 0–100 da etiqueta em curso, vindo do `printProgress$` da impressora. */
  private readonly loteAtualPct = signal(0);
  protected readonly nomeEmImpressao = signal<string | null>(null);
  /** O operador pediu para parar; `imprimirEmLote` consulta antes da próxima. */
  protected readonly parando = signal(false);

  protected readonly imprimindoLote = computed(() => this.imprimindo() && this.emLote());
  protected readonly progressoLote = computed(() =>
    percentualLote(this.loteConcluidas(), this.ingressos().length, this.loteAtualPct()),
  );

  /** Status vem por subscription — signal, senão o OnPush não repinta. */
  private readonly status = signal<NiimbotConnectionStatus>(this.niimbot.getConnectionStatus());

  /** Qual overlay usar. Escrito por listener de media query → signal. */
  protected readonly mobile = signal(false);

  protected readonly resumo = computed(() => resumoSelecao(this.ingressos()));
  protected readonly titulo = computed(() =>
    this.emLote() ? `Imprimir ${this.ingressos().length} etiquetas` : 'Imprimir etiqueta',
  );
  protected readonly rotuloImprimir = computed(() => {
    if (this.imprimindo()) return 'Imprimindo…';
    return this.emLote() ? `Imprimir ${this.ingressos().length}` : 'Imprimir';
  });
  protected readonly extras = computed(() => camposExtras(this.layout()));

  protected readonly impedido = computed(() =>
    impedimento({
      conectada: this.status().connected,
      ingressos: this.ingressos(),
      layout: this.layout(),
    }),
  );

  protected readonly detalheConexao = computed(() => {
    const s = this.status();
    const via = s.transport ? (s.transport === 'ble' ? 'Bluetooth' : 'Serial') : null;
    return [via, s.serialNumber].filter(Boolean).join(' · ');
  });

  constructor() {
    // <768px = breakpoint `md` do Tailwind, o mesmo que a página usa para
    // trocar tabela por cards e que o gerenciador de impressora usa.
    const mq = window.matchMedia('(max-width: 767px)');
    this.mobile.set(mq.matches);
    const onChange = (e: MediaQueryListEvent) => this.mobile.set(e.matches);
    mq.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => mq.removeEventListener('change', onChange));

    this.niimbot.connectionStatus$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((s) => this.status.set(s));

    // Cada printFromLayout do lote imprime UMA página, então page/pagesTotal
    // não medem o lote — quem mede é loteConcluidas. O que dá para aproveitar
    // é o percentual DENTRO da etiqueta, que faz a barra andar durante uma
    // impressão em vez de pular só quando ela acaba.
    this.niimbot.printProgress$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((p) => this.loteAtualPct.set(p.pagePrintProgress));
  }

  // ---- API pública ---------------------------------------------------------

  abrir(ingresso: IngressoEtiqueta): void {
    this.abrirLote([ingresso]);
  }

  /**
   * Abre para a seleção inteira. Lista vazia é aberta do mesmo jeito: o
   * impedimento `sem-selecao` explica na tela, o que é melhor que um `return`
   * calado — a seleção pode ter sobrado de uma página que já saiu de vista.
   */
  abrirLote(ingressos: readonly IngressoEtiqueta[]): void {
    this.ingressos.set([...ingressos]);
    this.valoresExtras.set({});
    this.loteConcluidas.set(0);
    this.loteAtualPct.set(0);
    this.nomeEmImpressao.set(null);
    this.parando.set(false);
    // Edição do canvas é do ingresso anterior; não pode vazar para o próximo.
    this.nomeEditado.set(null);
    this.loteEditado.set(null);
    // O editor emite `layoutChange` ao montar, mas o layout salvo já responde
    // "dá para imprimir?" antes disso — sem ele o aviso piscaria "sem layout".
    this.layout.set(layoutSalvo());
    // O serviço é global: pode ter conectado com este diálogo fechado.
    this.status.set(this.niimbot.getConnectionStatus());
    (this.mobile() ? this.folha() : this.dialogo()).open();
  }

  fechar(): void {
    this.dialogo().close(undefined);
    this.folha().close(undefined);
  }

  // ---- estado do editor ----------------------------------------------------

  protected aoMudarLayout(layout: LabelLayoutConfig): void {
    this.layout.set(layout);
    this.valoresExtras.update((atuais) => valoresIniciaisExtras(layout, atuais));
  }

  protected definirExtra(id: string, valor: string): void {
    this.valoresExtras.update((atuais) => ({ ...atuais, [id]: valor }));
  }

  // ---- impressão -----------------------------------------------------------

  protected imprimir(): void {
    const imp = this.impedido();
    const ingressos = this.ingressos();
    const layout = this.layout();
    if (imp || ingressos.length === 0 || !layout) {
      // O botão já está desabilitado por `impedido()`; este ramo é a rede caso
      // o estado mude entre o render e o clique. O que ele NÃO faz é o que o
      // modal antigo fazia: `return` calado.
      Swal.fire({ icon: 'warning', title: imp?.titulo ?? 'Não dá para imprimir', text: imp?.detalhe });
      return;
    }

    // Montado UMA vez para o lote inteiro: os valores extras são os mesmos em
    // todas as etiquetas (é o que a tela promete acima), e clonar o layout por
    // ingresso só gastaria trabalho para chegar ao mesmo desenho.
    const layoutFinal = montarLayout(layout, this.valoresExtras());

    // O nome corrigido no canvas é de UM ingresso. Repeti-lo num lote
    // carimbaria o mesmo nome em todas as etiquetas — o erro exato que a
    // sobrescrita existe para evitar.
    const sobrescritos = this.emLote()
      ? {}
      : { nomeParticipante: this.nomeEditado(), nomeLote: this.loteEditado() };

    this.imprimindo.set(true);
    this.loteConcluidas.set(0);
    this.loteAtualPct.set(0);
    this.parando.set(false);

    // Loader de tela cheia só no individual: no lote ele cobriria o progresso
    // que o rodapé desenha, e o rodapé é a única forma de parar.
    if (!this.emLote()) dispararLoader();

    imprimirEmLote(ingressos, {
      // firstValueFrom, e não subscribe: `imprimirEmLote` só serializa as
      // etiquetas porque dá `await` numa Promise por vez.
      imprimir: (ingresso) =>
        firstValueFrom(
          this.niimbot.printFromLayout(layoutFinal, dadosImpressao(ingresso, sobrescritos), 1),
        ),
      aoIniciar: (indice, ingresso) => {
        this.loteConcluidas.set(indice);
        this.loteAtualPct.set(0);
        this.nomeEmImpressao.set(ingresso.nomeParticipante ?? ingresso.codigoIngresso ?? null);
      },
      parar: () => this.parando(),
    }).then((resultado) => {
      this.imprimindo.set(false);
      this.nomeEmImpressao.set(null);
      this.loteConcluidas.set(resultado.impressas);

      if (resultado.impressas > 0) this.impresso.emit();

      if (!resultado.falha && !resultado.interrompido) {
        // Sucesso limpo: fecha e some. No individual o loader ainda está na
        // tela e precisa sair; no lote o closeLoader é no-op (nunca abriu).
        closeLoader();
        this.fechar();
        // Um Swal de sucesso por etiqueta seria um clique a mais entre um
        // ingresso e o próximo; no lote o número importa e vale o aviso.
        if (this.emLote()) {
          Swal.fire(avisoResultadoLote(resultado, ingressos.length));
        }
        return;
      }

      // Sem closeLoader aqui: ele chega depois do Swal abrir e dispensaria o
      // diálogo que acabou de aparecer. O Swal é a única saída.
      Swal.fire(avisoResultadoLote(resultado, ingressos.length));
    });
  }
}
